// The first screen: who the learner is, the one lesson to press to carry on,
// and the courses they are working through, each with its cover.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { get } from '../api'
import type { BlockedReason } from '../api'
import { useFormat, useT } from '../i18n'
import learn from '../i18n/learn'
import { Icon } from '../lib/ui'
import { CourseCover, LoadFailed, Loading, MoreSoon, SoonLine } from './Courses'
import type { CourseCard, Failure } from './Courses'
import '../styles/learn.css'
import '../styles/drafts.css'

type Standing = { points: number; rank: number; out_of: number }

type DashboardCourse = CourseCard & { points?: Standing }

type NextLecture = {
  course_id: string
  course_title: string
  section_title: string
  lecture_id: string
  title: string
  progress: number
  blocked: boolean
  blocked_reason?: BlockedReason
}

type Data = {
  learner: { full_name: string }
  courses: DashboardCourse[]
  continue?: NextLecture
}

export function Dashboard() {
  const t = useT(learn)
  const f = useFormat()
  const [data, setData] = useState<Data | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    setFailure(null)
    get<Data>('/learn/dashboard', ac.signal)
      .then(setData)
      .catch((err) => {
        if (!ac.signal.aborted) setFailure({ error: err })
      })
    return () => ac.abort()
  }, [attempt])

  if (failure) return <LoadFailed error={failure.error} onRetry={() => setAttempt((n) => n + 1)} />
  if (!data) return <Loading />

  const name = data.learner.full_name.trim()
  const first = name.split(/\s+/)[0] ?? ''
  const next = data.continue

  return (
    <div>
      <h1>{first ? t('greeting', { name: first }) : t('greetingNoName')}</h1>

      {next && !next.blocked && (
        <section className="notice notice--continue">
          <div className="grow">
            <h2>{t('continueTitle')}</h2>
            <p>
              {t('continueWhere', { course: next.course_title, module: next.section_title })}
              <br />
              <strong>{next.title}</strong>
            </p>
          </div>
          <Link className="button" to={`/lessons/${next.lecture_id}`}>
            <Icon name="play" size={18} />
            {t('openLesson')}
          </Link>
        </section>
      )}
      {next && next.blocked && (
        <section className="notice notice--warn notice--continue">
          <div className="grow">
            <h2>{t('waitingTitle')}</h2>
            <p>
              {t('continueWhere', { course: next.course_title, module: next.section_title })}
              <br />
              <strong>{next.title}</strong> — {t(reasonKey(next.blocked_reason))}
            </p>
          </div>
        </section>
      )}

      <h2 className="section-title">{t('myCourses')}</h2>
      {data.courses.length === 0 ? (
        <p className="muted">{t('noActiveCourses')}</p>
      ) : (
        <ul className="course-cards">
          {data.courses.map((c) => (
            <li key={c.id}>
              <Link className="card course-tile" to={`/courses/${c.id}`}>
                <div className="course-tile__media">
                  <CourseCover fileId={c.cover_file_id} title={c.title} />
                </div>
                <div className="card__body">
                  <h2>{c.title}</h2>
                  {c.total_lectures === 0 ? (
                    // Nothing published yet: no bar at 0% and no standing on a
                    // course nobody can have started.
                    <SoonLine />
                  ) : (
                    <>
                      <div
                        className="bar"
                        role="progressbar"
                        aria-label={t('progressOf', { title: c.title })}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={c.percent}
                      >
                        <div className="bar__fill" style={{ width: `${c.percent}%` }} />
                      </div>
                      <p className="muted">
                        {c.points
                          ? t('dashboardStanding', {
                              percent: f.number(c.percent),
                              points: c.points.points,
                              rank: f.number(c.points.rank),
                              outOf: f.number(c.points.out_of),
                            })
                          : t('percentDone', { percent: f.number(c.percent) })}
                      </p>
                      <MoreSoon count={c.draft_lectures ?? 0} />
                    </>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** The learn.ts key that says why a lesson is not open yet. */
export function reasonKey(reason?: BlockedReason) {
  switch (reason) {
    case 'progress':
      return 'reasonProgress'
    case 'homework':
      return 'reasonHomework'
    case 'schedule':
      return 'reasonSchedule'
    case 'access':
      return 'reasonAccess'
    case 'unpublished':
      return 'reasonUnpublished'
    default:
      return 'reasonUnknown'
  }
}
